import React from 'react';
import {
    Heading,
    Box,
    Text,
    Group,
    CheckOutlined,
    useBreakpoint,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import BackgroundImageFromAsset from 'components/BackgroundImageFromAsset/BackgroundImageFromAsset';
import FreemiumGatingCta from 'components/FreemiumGatingCta/FreemiumGatingCta';
import { Section } from 'components/Section/Section';
import { GradientBox } from 'components/GradientBox/GradientBox';
import { useEligibleForFreeTrial } from '@landr/pricing';
import { useAppSettings } from 'contexts/AppSettingsContext/AppSettingsContext';

const GroupItemUsp = ({ copy }: { copy: string }) => (
    <Group.Item>
        <Group gap="sm" marginBottom="xs">
            <Group.Item>
                <Box
                    display="inline-block"
                    color="icon.primary"
                    verticalAlign="middle"
                    boxSizing="content-box"
                >
                    <CheckOutlined size="md" />
                </Box>
            </Group.Item>
            <Group.Item>
                <Text fontSize="md" verticalAlign="middle">
                    {copy}
                </Text>
            </Group.Item>
        </Group>
    </Group.Item>
);

export const FreemiumGatingBanner = () => {
    const isSmallDesktop = useBreakpoint('lg');

    const { isEligibleForTrial } = useEligibleForFreeTrial();

    const assetBanner = isSmallDesktop
        ? 'freemiumGating/asset-banner-mobile'
        : 'freemiumGating/asset-banner';
    const assetObjectFit = 'cover';

    const { showFeaturedContent } = useAppSettings();

    if (!showFeaturedContent) {
        return null;
    }

    return (
        <Section>
            <Section.Content
                width="100%"
                alignItems="center"
                paddingLeft="none"
                paddingRight="none"
            >
                <GradientBox>
                    <Group
                        width="100%"
                        direction={
                            isSmallDesktop ? 'vertical-reverse' : 'horizontal'
                        }
                        gap="xxl"
                    >
                        <Group.Item
                            width="100%"
                            maxWidth={isSmallDesktop ? 'none' : '650px'}
                        >
                            <Box
                                pt={{ base: 'none', lg: '4xl' }}
                                pl={{ base: 'lg', lg: '3xl' }}
                                pb={{ base: '3xl', lg: '4xl' }}
                                pr={{ base: 'lg', lg: '4xl' }}
                            >
                                <Group gap="2xl" direction="vertical">
                                    <Group.Item>
                                        <Heading textStyle="title-lg" mb="none">
                                            <Trans>
                                                Get royalty-free samples and
                                                much more
                                            </Trans>
                                        </Heading>
                                    </Group.Item>
                                    <Group.Item>
                                        <Group direction="vertical" gap="4xs">
                                            <GroupItemUsp
                                                copy={t`Access a library of 3M+ Samples`}
                                            />
                                            <GroupItemUsp
                                                copy={t`Exclusive sample packs from top labels, artists and producers`}
                                            />
                                            <GroupItemUsp
                                                copy={t`Release music faster with 100% royalty-free sounds`}
                                            />
                                            <GroupItemUsp
                                                copy={t`A FREE sample pack to download every week`}
                                            />
                                            <GroupItemUsp
                                                copy={t`40+ Plugins, AI Mastering, Unlimited Distribution, Courses...`}
                                            />
                                        </Group>
                                    </Group.Item>
                                    <Group.Item
                                        textAlign={
                                            isSmallDesktop ? 'center' : 'left'
                                        }
                                    >
                                        {isEligibleForTrial ? (
                                            <FreemiumGatingCta
                                                analyticVariant="FreemiumGatingBanner"
                                                isRounded
                                                isEligibleForTrial={
                                                    isEligibleForTrial
                                                }
                                            >
                                                <Trans>
                                                    Start free 3-day trial
                                                </Trans>
                                            </FreemiumGatingCta>
                                        ) : (
                                            <FreemiumGatingCta
                                                analyticVariant="PartnerFreemiumGatingBanner"
                                                isRounded
                                                isEligibleForTrial={
                                                    isEligibleForTrial
                                                }
                                            >
                                                <Trans>
                                                    Unlock with a plan
                                                </Trans>
                                            </FreemiumGatingCta>
                                        )}
                                    </Group.Item>
                                </Group>
                            </Box>
                        </Group.Item>
                        <Group.Item width="100%">
                            <Box
                                width="100%"
                                height="100%"
                                position="relative"
                                p="none"
                            >
                                <BackgroundImageFromAsset
                                    image={assetBanner}
                                    filetype="png"
                                    lazyLoading={true}
                                    objectFit={assetObjectFit}
                                    dimensions={['2x', '3x', '4x']}
                                    bgPosition="centerLeft"
                                />
                            </Box>
                        </Group.Item>
                    </Group>
                </GradientBox>
            </Section.Content>
        </Section>
    );
};
